import RefluencedCreditIcon from "@/assets/colorable/refluenced_credit.svg?react";
import DottedPriceRow from "@/components/Campaign/common/DottedPriceRow";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import type {TBuyoutBundlePurchase, TBuyoutBundleType} from "@/hooks/useBuyoutBundlePurchase";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const SKELETON_CARD_COUNT = 4;

const AddMoreBuyoutsContent: FC<{purchase: TBuyoutBundlePurchase; type: TBuyoutBundleType}> = ({purchase, type}) => {
  const {t} = useTranslation(["payments", "common"]);
  const {
    amountToPay,
    bundles,
    credits,
    creditsToApply,
    creditsToRemain,
    currency,
    isLoading,
    selectedBundlePrice,
    selectedOption,
    setSelectedOption,
  } = purchase;
  const formatPrice = usePriceFormat({currency, fractionDigits: 2});

  return (
    <div className="flex w-full flex-col gap-4">
      <span className="text-neutral-grey-dark text-sm font-normal">{t(`${type as "EXTRA_BUYOUTS"}_choose`)}</span>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {isLoading
          ? Array.from({length: SKELETON_CARD_COUNT}).map((_, index) => (
              <div
                className="border-neutral-white-dark flex max-h-20.5 w-full items-center justify-between gap-2 rounded-md border bg-white p-5"
                key={index}>
                <div className="flex w-full flex-col items-start gap-1">
                  <div className="flex h-6 w-full items-center">
                    <SkeletonLoader className="h-4 max-w-32" />
                  </div>
                  <div className="flex h-5 w-full items-center">
                    <SkeletonLoader className="h-3 max-w-48" />
                  </div>
                </div>
                <SkeletonLoader className="size-5 shrink-0 rounded-full" />
              </div>
            ))
          : bundles.map((bundle, index) => {
              const price = +(bundle.prices[0].price * (1 + (bundle.vat ?? 0) / 100)).toFixed(2);
              const count =
                type === "EXTRA_BUYOUTS" ? bundle.buyout : type === "EXTRA_SEATS" ? bundle.seats : bundle.campaigns;

              return (
                <button
                  onClick={() => {
                    if (selectedOption === index) setSelectedOption(null);
                    else setSelectedOption(index);
                  }}
                  key={index}
                  className={
                    "group border-neutral-white-dark hover:border-primary-medium-solid relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-white p-5 transition-all duration-150 hover:shadow-md " +
                    (index === selectedOption ? "border-primary-dark-solid" : "")
                  }>
                  {bundle.is_most_popular && (
                    <div className="border-neutral-white-dark absolute -top-4 right-0 left-0 mx-auto w-min rounded-full border bg-white px-3 py-1 shadow-xs">
                      <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-sm whitespace-nowrap text-transparent">
                        {t(`${type as "EXTRA_BUYOUTS"}_most_popular`)}
                      </span>
                    </div>
                  )}
                  <div className="flex w-full flex-col items-start gap-1">
                    <span
                      className={
                        "group-hover:primary-medium-solid text-neutral-black group-hover:text-primary-medium-solid leading-none font-semibold transition-all duration-150 " +
                        (selectedOption === index ? "text-primary-dark-solid" : "")
                      }>
                      {t(`${type as "EXTRA_BUYOUTS"}_x`, {count})}
                    </span>
                    <span className="text-neutral-grey-dark text-left text-sm whitespace-nowrap">
                      {formatPrice(price)}{" "}
                      {t(`${type as "EXTRA_BUYOUTS"}_disclaimer`, {
                        count: +(price / count).toFixed(2),
                      })}
                    </span>
                  </div>
                  <div
                    className={
                      "border-neutral-grey-light group-hover:border-primary-medium-solid size-5 shrink-0 rounded-full border transition-all duration-150 " +
                      (selectedOption === index ? "border-primary-dark-solid drop-shadow-xs" : "")
                    }>
                    <div
                      className={
                        "h-full w-full rounded-full border border-white transition-all duration-150 " +
                        (selectedOption === index
                          ? "bg-primary-dark-solid group-hover:bg-primary-medium-solid"
                          : "bg-white")
                      }></div>
                  </div>
                </button>
              );
            })}
      </div>
      {!!credits && (
        <div className="bg-neutral-grey-lightest flex flex-col gap-3 rounded-xl p-5">
          <DottedPriceRow
            currency={currency}
            label={t("bundle_cost")}
            value={selectedBundlePrice}
            valueClassName="font-normal"
          />
          <DottedPriceRow
            icon={<RefluencedCreditIcon className="text-neutral-black size-3.5 shrink-0" />}
            label={t("credits_applied")}
            showCurrency={false}
            value={creditsToApply}
            valueClassName="font-normal"
          />
          <div className="bg-neutral-grey-light-dark h-px w-full" />
          <DottedPriceRow
            currency={currency}
            label={t("you_pay")}
            value={amountToPay}
            labelClassName="text-base text-neutral-black"
            valueClassName="text-base"
          />
          <div className="flex items-center justify-end gap-1">
            <RefluencedCreditIcon className="text-neutral-black size-3.5 shrink-0" />
            <span className="text-xs">
              {t("credits_remaining")}: {creditsToRemain}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddMoreBuyoutsContent;
